import { useCallback, useState } from 'react';

import { MissingHealthcareProviderActiveP1CertificatesFault } from '@dreryk/egabinet-p1-frontend-api';
import {
   ElectronicReferralApprovalResult,
   P1HandshakeFault,
   ReferralVerificationFault,
} from '@dreryk/egabinet-referral-frontend-api';
import {
   InvalidPrivateKeyFault,
   NoValidCertificateFoundFault,
   PrivateKeyNotDecodedFault,
} from '@dreryk/egabinet-truststore-published-language-api';
import { VisitReferralToHospitalApproveCommand } from '@dreryk/egabinet-visit-frontend-api';

import { useCqrsCommand, useDataSyncing } from '@dreryk/command-query-react-common';
import {
   ActionBar,
   ButtonVariant,
   DataSyncWaitingEventInterceptor,
   dispatchSnackbarNegativeEvent,
   usePushBack,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';
import { useDraftRemoveByTokenCqrsCommand } from '@dreryk/react-draft';

import { useAccountAuthorizationModal, useCertificateFaultHandlerMap } from '~libs.desktop.shared.account';
import { visitPaths } from '~libs.desktop.shared.paths';
import { PatientNfzDepartmentUpdateShowModalEventInterceptor } from '~libs.desktop.shared.patient';

import { getReferralDraftToken } from '../../referral.draft';
import {
   dispatchElectronicReferralCodeSnackbarPositiveEvent,
   getReferralVerificationResultErrorList,
} from '../../shared/common.utils';
import { ElectronicReferralFailureModal } from '../../shared/ElectronicReferralFailureModal';
import { referralTranslationKeys } from '../../translations';
import {
   ReferralToHospitalFormBean,
   ReferralToHospitalFormController,
} from '../ReferralToHsopitalForm/ReferralToHospitalForm.types';

import { useReferralToHospitalConfirmAndPrintCommand } from './ReferralToHospitalSendAction.hooks';
import { mapReferralToHospitalFormBeanToReferralCommandDetails } from './ReferralToHospitalSendAction.utils';

type ElectronicReferralToHospitalSendActionProps = {
   readonly handleSubmit: ReferralToHospitalFormController['handleSubmit'];
   readonly trigger: ReferralToHospitalFormController['trigger'];
   readonly onSuccess: VoidFunction;
   readonly visitId: number;
   readonly patientId: number;
};

// eslint-disable-next-line max-lines-per-function
export const ElectronicReferralToHospitalSendAction = (
   props: ElectronicReferralToHospitalSendActionProps
): JSX.Element => {
   const [referralVerificationErrors, setReferralVerificationErrors] = useState<string[] | undefined>();
   const { openAccountAuthorizationModal, renderAccountAuthorizationnModal } = useAccountAuthorizationModal();
   const push = usePushBack();

   const handleReferralVerificationErrors = useCallback((fault?: ReferralVerificationFault) => {
      setReferralVerificationErrors(fault ? getReferralVerificationResultErrorList(fault) : []);
   }, []);

   const { executeAsync: removeDraft } = useDraftRemoveByTokenCqrsCommand();
   const removeElectronicReferralToHospitalDraft = useCallback(
      async () => removeDraft({ token: getReferralDraftToken('electronicReferralToHospital', props.visitId) }),
      [props.visitId, removeDraft]
   );

   const { executeAsync: sendReferralToHospital } = useCqrsCommand<
      ElectronicReferralApprovalResult,
      ReferralToHospitalFormBean,
      | InvalidPrivateKeyFault
      | NoValidCertificateFoundFault
      | PrivateKeyNotDecodedFault
      | ReferralVerificationFault
      | MissingHealthcareProviderActiveP1CertificatesFault
      | P1HandshakeFault
   >(
      (parameters) => {
         const command = new VisitReferralToHospitalApproveCommand();
         command.visitId = props.visitId;
         command.referralDetails = mapReferralToHospitalFormBeanToReferralCommandDetails(parameters, true);
         return command;
      },
      {
         onSuccess: (result) => {
            dispatchElectronicReferralCodeSnackbarPositiveEvent(result.code);
            props.onSuccess();
            removeElectronicReferralToHospitalDraft();
            push(
               visitPaths.getVisitInterviewPath({
                  params: {
                     visitId: props.visitId,
                  },
               })
            );
         },
         onFault: {
            ...useCertificateFaultHandlerMap({
               onAuthenticationRequired: openAccountAuthorizationModal,
            }),
            'egabinet-referral-frontend-api:ReferralVerificationFault': handleReferralVerificationErrors,
            'egabinet-p1-frontend-api:MissingHealthcareProviderActiveP1CertificatesFault': () =>
               dispatchSnackbarNegativeEvent(referralTranslationKeys.missingHealthProviderCertificates),
            'egabinet-referral-frontend-api:P1HandshakeFault': () => {
               dispatchSnackbarNegativeEvent(referralTranslationKeys.certificateErrorOccurred);
            },
         },
      }
   );
   const { executeAsync: confirmAndPrintReferralToHospital } = useReferralToHospitalConfirmAndPrintCommand(
      props.visitId,
      async () => {
         props.onSuccess();
         await removeElectronicReferralToHospitalDraft();
      }
   );
   return (
      <>
         <ValidateFormEventInterceptor trigger={props.trigger}>
            <DataSyncWaitingEventInterceptor waitUntil={useDataSyncing()}>
               {(waiting) => (
                  <PatientNfzDepartmentUpdateShowModalEventInterceptor
                     patientId={props.patientId}
                     modalLabel={referralTranslationKeys.missingPatientNFZInfo}
                  >
                     <ActionBar.PrimaryButton
                        variant={ButtonVariant.PRIMARY}
                        onClick={props.handleSubmit(sendReferralToHospital)}
                        label={referralTranslationKeys.sendElectronicReferral}
                        testId={'sendReferral'}
                        disabled={waiting}
                        blockingOperation={referralTranslationKeys.submittingElectronicReferral}
                     />
                  </PatientNfzDepartmentUpdateShowModalEventInterceptor>
               )}
            </DataSyncWaitingEventInterceptor>
         </ValidateFormEventInterceptor>
         {renderAccountAuthorizationnModal({
            onAuthorization: props.handleSubmit(sendReferralToHospital),
         })}
         {referralVerificationErrors ? (
            <ElectronicReferralFailureModal
               onConfirm={props.handleSubmit(confirmAndPrintReferralToHospital)}
               onCancel={() => setReferralVerificationErrors(undefined)}
               errors={referralVerificationErrors}
            />
         ) : null}
      </>
   );
};
